import React, { useState } from 'react';
import { Button } from './ui/button';
import { Loader2, ArrowRight, CheckCircle2, Shield, CreditCard, Calendar, Clock, Phone } from 'lucide-react';

/**
 * Book a Demo Slot section.
 * Dual paths: (A) Free Demo Booking — primary; (B) Direct ₹3,000 Admission — secondary.
 * Light, premium aesthetic — Apple / MasterClass inspired.
 */
const themeAccents = {
  violet:  { primary: 'from-violet-600 to-indigo-600',   ring: 'ring-violet-200',   chip: 'bg-violet-100 text-violet-700',   text: 'text-violet-700' },
  blue:    { primary: 'from-blue-600 to-cyan-600',       ring: 'ring-blue-200',     chip: 'bg-blue-100 text-blue-700',       text: 'text-blue-700' },
  orange:  { primary: 'from-orange-500 to-amber-600',    ring: 'ring-orange-200',   chip: 'bg-orange-100 text-orange-700',   text: 'text-orange-700' },
  emerald: { primary: 'from-emerald-500 to-teal-600',    ring: 'ring-emerald-200',  chip: 'bg-emerald-100 text-emerald-700', text: 'text-emerald-700' },
  yellow:  { primary: 'from-amber-500 to-orange-600',    ring: 'ring-amber-200',    chip: 'bg-amber-100 text-amber-800',     text: 'text-amber-800' }
};

const TIME_SLOTS = [
  'Weekday Morning (10am – 12pm)',
  'Weekday Afternoon (2pm – 5pm)',
  'Weekday Evening (6pm – 8pm)',
  'Weekend Morning (10am – 12pm)',
  'Weekend Afternoon (2pm – 5pm)',
  'Weekend Evening (6pm – 8pm)',
  'Anytime — Counselor can call me'
];

const AdmissionEnrollment = ({ courseName = 'Course', theme = 'violet', source = 'course-page' }) => {
  const t = themeAccents[theme] || themeAccents.violet;
  const [form, setForm] = useState({
    name: '', email: '', phone: '', course: courseName, preferred_date: '', time_slot: TIME_SLOTS[2]
  });
  const [status, setStatus] = useState('idle'); // idle | loading | ok | error
  const [error, setError] = useState('');
  const [payLoading, setPayLoading] = useState(false);

  const submitDemo = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      setError('Please fill in name, email and phone.');
      return;
    }
    if (!form.preferred_date) {
      setError('Please choose your preferred demo date.');
      return;
    }
    setError('');
    setStatus('loading');
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: `Preferred Date: ${form.preferred_date} | Preferred Slot: ${form.time_slot} | Interested Course: ${form.course}`,
          course: form.course,
          source: `${source}-demo`,
          time_slot: form.time_slot,
          preferred_date: form.preferred_date
        })
      });
      if (!res.ok) throw new Error('submit failed');
      if (window.gtag) window.gtag('event', 'demo_booked', { course: form.course });
      if (window.fbq) window.fbq('track', 'Lead', { content_name: form.course });
      setStatus('ok');
    } catch (_) {
      setStatus('error');
      setError('Something went wrong. Please WhatsApp us instead.');
    }
  };

  const payDirect = async () => {
    if (!form.name || !form.email || !form.phone) {
      setError('Please fill name, email and phone before paying.');
      return;
    }
    setError('');
    setPayLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/payments/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstname: form.name,
          email: form.email,
          phone: form.phone,
          course_name: `${form.course} - Admission Fee`,
          amount: 3000
        })
      });
      if (!res.ok) throw new Error('payu failed');
      const html = await res.text();
      const newWin = window.open('', '_self');
      newWin.document.write(html);
      newWin.document.close();
      if (window.gtag) window.gtag('event', 'admission_payment_initiated', { course: form.course, value: 3000 });
      if (window.fbq) window.fbq('track', 'InitiateCheckout', { content_name: form.course, value: 3000, currency: 'INR' });
    } catch (_) {
      setError('Payment gateway error. Please try again or WhatsApp us.');
      setPayLoading(false);
    }
  };

  return (
    <section id="enroll" data-testid="admission-enrollment" className="py-24 px-6 bg-gradient-to-b from-white via-slate-50 to-white relative overflow-hidden">
      {/* Soft decorative accents */}
      <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${t.primary} opacity-70`}></div>
      <div className="absolute top-20 -left-32 w-[400px] h-[400px] rounded-full bg-violet-200/30 blur-[140px]"></div>
      <div className="absolute bottom-10 -right-32 w-[400px] h-[400px] rounded-full bg-amber-200/30 blur-[140px]"></div>

      <div className="container mx-auto max-w-5xl relative z-10">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 ${t.chip} rounded-full px-4 py-1.5 mb-4`}>
            <Calendar className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">Book a Demo Slot</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
            See a Live Demo, <span className={`text-transparent bg-clip-text bg-gradient-to-r ${t.primary}`}>Then Decide</span>
          </h2>
          <p className="text-base md:text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
            Talk to our counselor on a free 30-minute demo session. Get all your questions answered before you enroll.
          </p>
          <p className="text-sm text-slate-500 mt-3 inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
            Next Status: <span className="font-bold text-slate-800">Admissions Open</span> · Seats filling fast
          </p>
        </div>

        <div className={`relative bg-white rounded-3xl shadow-2xl ${t.ring} ring-1 border border-slate-100 overflow-hidden`}>
          <div className="grid lg:grid-cols-5">
            {/* LEFT — value props */}
            <div className="lg:col-span-2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 md:p-10 text-white relative overflow-hidden">
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(251,191,36,0.4) 0%, transparent 50%)' }}></div>
              <div className="relative">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-amber-300 mb-3">Your Free Demo Includes</p>
                <h3 className="text-2xl font-black mb-6 leading-tight">A complete walkthrough of <span className="text-amber-300">{courseName}</span></h3>
                <ul className="space-y-3 mb-8">
                  {[
                    'Curriculum walk-through',
                    'Sample projects you\u2019ll build',
                    'Career outcomes & salary ranges',
                    'EMI options & batch dates',
                    'Q&A with industry counselor'
                  ].map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-200">
                      <CheckCircle2 className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
                  <p className="text-xs font-bold uppercase tracking-widest text-amber-300 mb-2">Ready to enroll?</p>
                  <p className="text-sm text-slate-200 mb-4">Skip the demo &amp; pay the <span className="font-black text-white">&#8377;3,000 admission fee</span> directly to reserve your seat.</p>
                  <button
                    onClick={payDirect}
                    disabled={payLoading}
                    data-testid="pay-admission-btn"
                    className={`w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r ${t.primary} text-white font-black py-3 rounded-xl text-sm shadow-lg hover:scale-[1.02] transition-transform disabled:opacity-60`}
                  >
                    {payLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4" /> Pay &#8377;3,000 Direct Admission</>}
                  </button>
                  <p className="text-[10px] text-slate-400 mt-3 text-center"><Shield className="w-3 h-3 inline mr-1" /> 100% Secure PayU · UPI · Cards · Net Banking</p>
                </div>
              </div>
            </div>

            {/* RIGHT — form */}
            <div className="lg:col-span-3 p-8 md:p-10 bg-white">
              {status === 'ok' ? (
                <div className="text-center py-8" data-testid="demo-success">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-9 h-9 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 mb-2">You're booked! 🎉</h3>
                  <p className="text-slate-600 mb-6">A confirmation email has been sent to <strong>{form.email}</strong>. Our counselor will WhatsApp you within 24 hours to confirm your demo for <strong>{form.preferred_date}</strong> · <strong>{form.time_slot}</strong>.</p>
                  <a
                    href={`https://wa.me/919891550666?text=${encodeURIComponent('Hi, I just booked a demo for ' + form.course)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-3 rounded-xl"
                  >
                    <Phone className="w-4 h-4" /> Message Counselor on WhatsApp
                  </a>
                </div>
              ) : (
                <form onSubmit={submitDemo} className="space-y-4" data-testid="demo-form">
                  <div>
                    <h3 className="text-2xl font-black text-slate-900 mb-1">Reserve My Demo Session</h3>
                    <p className="text-sm text-slate-500">No payment now. Industry-oriented training. Free career counseling.</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-bold text-slate-600 mb-1 block">Full Name *</label>
                      <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" data-testid="demo-name" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-violet-500 focus:bg-white" />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-600 mb-1 block">Phone *</label>
                      <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 9876543210" data-testid="demo-phone" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-violet-500 focus:bg-white" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-600 mb-1 block">Email *</label>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="demo-email" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-violet-500 focus:bg-white" />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-600 mb-1 block">Interested Course *</label>
                    <select value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} data-testid="demo-course" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:outline-none focus:border-violet-500 focus:bg-white">
                      {[courseName, 'AI Bootcamp', 'Performance Marketing Course', 'Film Making & Cinematic Animation', 'Game Development Course', 'Jewellery Designing in Blender', 'Blender & Character Design', 'Not Sure Yet'].filter((v, i, a) => a.indexOf(v) === i).map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-bold text-slate-600 mb-1 block flex items-center gap-1"><Calendar className="w-3 h-3" /> Preferred Date *</label>
                      <input
                        required
                        type="date"
                        min={new Date().toISOString().split('T')[0]}
                        max={new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0]}
                        value={form.preferred_date}
                        onChange={(e) => setForm({ ...form, preferred_date: e.target.value })}
                        data-testid="demo-date"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:outline-none focus:border-violet-500 focus:bg-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-slate-600 mb-1 block flex items-center gap-1"><Clock className="w-3 h-3" /> Preferred Time Slot *</label>
                      <select value={form.time_slot} onChange={(e) => setForm({ ...form, time_slot: e.target.value })} data-testid="demo-time-slot" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:outline-none focus:border-violet-500 focus:bg-white">
                        {TIME_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>

                  {error && <p className="text-red-600 text-sm">{error}</p>}

                  <Button type="submit" disabled={status === 'loading'} data-testid="demo-submit" className={`w-full bg-gradient-to-r ${t.primary} text-white font-black py-6 rounded-xl text-base shadow-xl hover:scale-[1.01] transition`}>
                    {status === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Reserve My Demo Session <ArrowRight className="w-5 h-5 ml-2" /></>}
                  </Button>

                  <p className="text-[11px] text-slate-400 text-center">By submitting, you'll receive a confirmation email from us within minutes.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdmissionEnrollment;
